import { postAgenda } from '../../services/agenda';
import {
  buildDateTimeString,
  buildManagedFiles,
  isBlank,
  normalizeDateValue,
  normalizeServerFiles,
  normalizeTimeValue,
  NEW_APPOINTMENT_VALUE,
  pickStringValue,
  toOption,
} from './shared';

export const BOOKING_PERSISTENCE_ERROR_NAME = 'BookingPersistenceError';
export const BOOKING_SLOT_UNAVAILABLE_REASON_CODE =
  'SCHEDULING_SLOT_UNAVAILABLE';
export const DEFAULT_BOOKING_PERSISTENCE_ERROR_MESSAGE =
  'No se pudo guardar la cita';
export const DEFAULT_BOOKING_SLOT_UNAVAILABLE_MESSAGE =
  'La hora seleccionada ya no está disponible para este profesional.';

export const normalizeBookingDraft = (raw = {}) => ({
  idCita: raw?.idCita ?? null,
  document: raw?.document ?? '',
  area: toOption(raw?.area),
  service: toOption(raw?.service),
  professional: toOption(raw?.professional),
  areaAtencion: toOption(raw?.areaAtencion ?? raw?.idAreaAtencion),
  puntoLatitud:
    raw?.puntoLatitud != null && raw?.puntoLatitud !== ""
      ? Number(raw.puntoLatitud)
      : null,
  puntoLongitud:
    raw?.puntoLongitud != null && raw?.puntoLongitud !== ""
      ? Number(raw.puntoLongitud)
      : null,
  puntoDireccion: raw?.puntoDireccion ?? "",
  puntoReferencia: raw?.puntoReferencia ?? "",
  date: normalizeDateValue(raw?.date),
  time: normalizeTimeValue(raw?.time),
  statusId: raw?.statusId ?? '',
  insurer: toOption(raw?.insurer),
  policyNumber: raw?.policyNumber ?? '',
  province: toOption(raw?.province),
  district: toOption(raw?.district),
  corregimiento: toOption(raw?.corregimiento),
  address: raw?.address ?? '',
  corporateClient: toOption(raw?.corporateClient),
  teleconsulta: Boolean(raw?.teleconsulta),
  ignorarTurno: raw?.ignorarTurno === true,
  action: raw?.action ?? '',
  managedFiles: buildManagedFiles(raw?.managedFiles),
});

export const createBookingFormData = ({
  draft,
  user,
  patientId,
  bookingId,
  overrides = {},
}) => {
  const normalized = normalizeBookingDraft({
    ...draft,
    idCita: bookingId ?? draft?.idCita,
  });
  const form = new FormData();
  const resolvedBookingId =
    bookingId ?? normalized.idCita ?? NEW_APPOINTMENT_VALUE;
  const op =
    overrides.op ??
    (String(resolvedBookingId) === NEW_APPOINTMENT_VALUE
      ? 'agregar'
      : 'actualizar');

  form.append('op', op);
  form.append('idCita', resolvedBookingId ?? NEW_APPOINTMENT_VALUE);
  form.append(
    'idProfesional',
    overrides.professionalId ?? pickStringValue(normalized.professional)
  );
  form.append('idusuario', user.id);
  form.append('idpaciente', patientId ?? '');
  form.append('idcentro', user.idcentro);
  form.append(
    'fechainicia',
    buildDateTimeString(normalized.date, normalized.time)
  );
  form.append('indice', normalizeTimeValue(normalized.time));
  form.append('idServicio', pickStringValue(normalized.service));
  form.append('idprovincia', pickStringValue(normalized.province));
  form.append('iddistrito', pickStringValue(normalized.district));
  form.append('idcorregimiento', pickStringValue(normalized.corregimiento));
  form.append('domicilio', normalized.address ?? '');
  form.append('idaseguradora', pickStringValue(normalized.insurer));
  form.append('numpoliza', normalized.policyNumber ?? '');
  form.append('idestado', normalized.statusId ?? '');
  const corporateClientId = pickStringValue(normalized.corporateClient);
  form.append('idClienteCorporativo', corporateClientId);
  form.append('idclientecorporativo', corporateClientId);
  form.append('documento', normalized.document ?? '');
  form.append('idMedico', pickStringValue(normalized.professional));
  form.append('idCentro', user.idcentro);
  form.append('esTeleconsulta', normalized.teleconsulta ? 'true' : 'false');
  form.append('ignorarTurno', normalized.ignorarTurno ? 'true' : 'false');
  form.append('accion', overrides.action ?? normalized.action ?? '');
  form.append(
    "idAreaAtencion",
    pickStringValue(normalized.areaAtencion) || ""
  );
  form.append(
    "puntoLatitud",
    normalized.puntoLatitud != null ? String(normalized.puntoLatitud) : ""
  );
  form.append(
    "puntoLongitud",
    normalized.puntoLongitud != null ? String(normalized.puntoLongitud) : ""
  );
  form.append("puntoDireccion", normalized.puntoDireccion ?? "");
  form.append("puntoReferencia", normalized.puntoReferencia ?? "");

  const newFiles = normalized.managedFiles.newFiles ?? [];

  if (newFiles.length > 0) {
    newFiles.forEach((file, index) => {
      form.append(`file_${index}`, file);
    });
  } else {
    form.append('file', []);
  }
  form.append('guia', newFiles.length);
  form.append(
    'listFiles',
    JSON.stringify(normalizeServerFiles(normalized.managedFiles.serverFiles))
  );

  return form;
};

const buildBookingPersistenceError = ({
  message,
  response,
  payload,
  status,
}) => {
  const error = new Error(message || DEFAULT_BOOKING_PERSISTENCE_ERROR_MESSAGE);
  error.name = BOOKING_PERSISTENCE_ERROR_NAME;
  error.isBookingPersistenceError = true;
  error.response = response;
  error.payload = payload;
  error.status = status ?? response?.status ?? null;
  error.rsp = payload?.rsp ?? null;
  error.reasonCode = payload?.reasonCode ?? payload?.data?.reasonCode ?? null;
  error.wasNotified = false;
  return error;
};

export const isBookingPersistenceError = (error) =>
  error?.isBookingPersistenceError === true ||
  error?.name === BOOKING_PERSISTENCE_ERROR_NAME;

export const isSchedulingSlotUnavailableBookingError = (error) =>
  isBookingPersistenceError(error) &&
  error?.reasonCode === BOOKING_SLOT_UNAVAILABLE_REASON_CODE;

export const resolveBookingPersistenceErrorMessage = (
  error,
  fallbackMessage = DEFAULT_BOOKING_PERSISTENCE_ERROR_MESSAGE
) => {
  if (isSchedulingSlotUnavailableBookingError(error)) {
    return (
      error?.payload?.msg ||
      error?.message ||
      DEFAULT_BOOKING_SLOT_UNAVAILABLE_MESSAGE
    );
  }

  return error?.payload?.msg || error?.message || fallbackMessage;
};

export const isBookingPersistenceErrorHandled = (error) =>
  isBookingPersistenceError(error) && error?.wasNotified === true;

export const markBookingPersistenceErrorHandled = (error) => {
  if (isBookingPersistenceError(error)) {
    error.wasNotified = true;
  }

  return error;
};

export const persistBookingDraft = async ({
  draft,
  user,
  patientId,
  bookingId,
  overrides = {},
}) => {
  const response = await postAgenda(
    createBookingFormData({
      draft,
      user,
      patientId,
      bookingId,
      overrides,
    })
  );

  const { status, data: payload } = response;
  if (status !== 200) {
    throw buildBookingPersistenceError({
      message: payload?.msg || 'No se pudo guardar la cita',
      response,
      payload,
      status,
    });
  }

  const { rsp, id, msg } = payload ?? {};
  if (rsp !== 1) {
    throw buildBookingPersistenceError({
      message: msg || 'No se pudo guardar la cita',
      response,
      payload,
      status,
    });
  }

  return { id, msg, payload };
};

export const changeBookingStatus = async ({ idCita, idestado }) => {
  const form = new FormData();
  form.append('op', 'actualizarEstatusCita');
  form.append('idCita', idCita);
  form.append('idestado', idestado);

  const response = await postAgenda(form);
  const { status, data } = response;
  if (status !== 200 || data?.rsp !== 1) {
    throw new Error(data?.msg || 'No se pudo actualizar el estado de la cita');
  }

  return data;
};

export const verifyBookingStart = async ({ bookingId, professionalId }) => {
  const form = new FormData();
  form.append('op', 'verificarIniciarConsulta');
  form.append('id', bookingId);
  form.append('idMedico', professionalId);

  const response = await postAgenda(form);
  const { status, data } = response;
  if (status !== 200 || data?.rsp !== 1) {
    throw new Error(data?.msg || 'No se pudo verificar la cita');
  }

  return data;
};

export const isCreateBooking = (bookingId) =>
  isBlank(bookingId) || String(bookingId) === NEW_APPOINTMENT_VALUE;
